import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-900 px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-lg p-8">
              <div className="flex justify-center mb-6">
                <div className="size-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="size-8 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-4">
                Oops! Something went wrong
              </h1>

              <p className="text-zinc-600 dark:text-zinc-300 mb-6">
                We encountered an unexpected error. Please try refreshing the page.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mb-6 p-4 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-left">
                  <p className="text-sm font-mono text-zinc-800 dark:text-zinc-200 break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-x-2 bg-primary-600 hover:bg-primary-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200"
              >
                <RefreshCw className="size-4" />
                <span>Refresh Page</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
